import type { ClientGeoArea } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useEffect, type FC } from 'react';

import { convertPosition } from '@/features/geo-area/utils/map';
import type {
  FitToViewMessageData,
  GeoAreaMarker,
  SetAnimateMessageData,
  SetCenterMessageData,
  SetMarkersMessageData,
  SetRangeMessageData,
  SetThemeMessageData
} from '@/features/geo-area/utils/mapMessages';
import { Events, MarkerType } from '@/features/geo-area/utils/mapMessages';
import { useEmbeddedWebpageContext } from '@/shared/components/embedded/useEmbeddedWebpageContext';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  homeGeoArea: ClientGeoArea;
  selectedIds?: ClientGeoArea['id'][];
  visibleGeoAreas: ClientGeoArea[];
  range: number;
};

// TODO: color of check icon

export const SubscriptionMapContent: FC<Props> = ({
  homeGeoArea,
  selectedIds,
  visibleGeoAreas,
  range
}) => {
  const { sendMessage } = useEmbeddedWebpageContext();
  const {
    colors: { primary, error }
  } = useAppTheme();

  useEffect(() => {
    sendMessage({
      event: Events.SET_THEME,
      payload: { markerColor: primary, rangeColor: '#00A2814F' }
    } satisfies SetThemeMessageData);
  }, [primary, sendMessage]);

  useEffect(() => {
    sendMessage({
      event: Events.SET_ANIMATE,
      payload: { animate: true }
    } satisfies SetAnimateMessageData);
    sendMessage({
      event: Events.SET_CENTER,
      payload: { position: convertPosition(homeGeoArea.center) }
    } satisfies SetCenterMessageData);
    sendMessage({
      event: Events.FIT_TO_VIEW,
      payload: { center: true, markers: true, range: true }
    } satisfies FitToViewMessageData);
  }, [sendMessage, homeGeoArea]);

  useEffect(() => {
    const geoAreaToMarker = (
      { id, name, center }: ClientGeoArea,
      type: MarkerType,
      size: number,
      color?: string
    ): GeoAreaMarker => ({
      id,
      type,
      name,
      position: convertPosition(center),
      size,
      color
    });

    const markers: GeoAreaMarker[] = [
      geoAreaToMarker(homeGeoArea, MarkerType.HOME, 40, error),
      ...visibleGeoAreas.map((visibleGeoArea) =>
        selectedIds?.includes(visibleGeoArea.id)
          ? geoAreaToMarker(visibleGeoArea, MarkerType.CHECK, 24, 'none')
          : geoAreaToMarker(visibleGeoArea, MarkerType.ADD, 24)
      )
    ];
    sendMessage({
      event: Events.SET_MARKERS,
      payload: { markers }
    } satisfies SetMarkersMessageData);
  }, [sendMessage, homeGeoArea, visibleGeoAreas, error, selectedIds, primary]);

  useEffect(() => {
    sendMessage({
      event: Events.SET_RANGE,
      payload: { meters: range }
    } satisfies SetRangeMessageData);
    sendMessage({
      event: Events.FIT_TO_VIEW,
      payload: { center: true, markers: true, range: true }
    } satisfies FitToViewMessageData);
  }, [sendMessage, range]);

  return <></>;
};
